// src/oura/ui/PageTabs.tsx
//
// Underlined page tabs: the house style for a page's top-level sections, one
// address per tab. The merchant's Visibility (AI Visibility · Website) and the
// staff Documents page (Client documents · Knowledge) both draw these, so the
// two read the same.

import type { LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { FOCUS_RING } from "./tokens";

export interface PageTab<K extends string> {
    key: K;
    label: string;
    icon: LucideIcon;
}

export function PageTabs<K extends string>({
    label,
    items,
    value,
    onOpen,
}: {
    /** The nav's accessible name: "Visibility", "Documents". */
    label: string;
    items: PageTab<K>[];
    value: K;
    onOpen: (key: K) => void;
}) {
    return (
        <nav aria-label={label} className="mb-5 flex gap-1 overflow-x-auto overflow-y-hidden scrollbar-hide border-b border-gray-200">
            {items.map(({ key, label: text, icon: Icon }) => {
                const on = key === value;
                return (
                    <button
                        key={key}
                        type="button"
                        aria-current={on ? "page" : undefined}
                        onClick={() => {
                            if (!on) onOpen(key);
                        }}
                        className={cn(
                            "-mb-px flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                            on ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-700",
                            FOCUS_RING,
                        )}
                    >
                        <Icon aria-hidden className="h-3.5 w-3.5" />
                        {text}
                    </button>
                );
            })}
        </nav>
    );
}
